import { Component } from '@theme/component';
import { debounce, isClickedOutside, onAnimationEnd } from '@theme/utilities';

/**
 * A custom element that manages a dialog.
 *
 * @typedef {object} Refs
 * @property {HTMLDialogElement} dialog – The dialog element.
 *
 * @extends Component<Refs>
 */
export class DialogComponent extends Component {
  requiredRefs = ['dialog'];

  connectedCallback() {
    super.connectedCallback();

    if (this.minWidth || this.maxWidth) {
      window.addEventListener('resize', this.#handleResize);
    }
  }

  disconnectedCallback() {
    super.disconnectedCallback();
    if (this.minWidth || this.maxWidth) {
      window.removeEventListener('resize', this.#handleResize);
    }
  }

  #handleResize = debounce(() => {
    const { minWidth, maxWidth } = this;

    if (!minWidth && !maxWidth) return;

    const windowWidth = window.innerWidth;
    if (windowWidth < minWidth || windowWidth > maxWidth) {
      this.closeDialog();
    }
  }, 50);

  // CH: native `#previousScrollY` + the position:fixed body-lock below are disabled.
  // We rely on the simpler `html[scroll-lock] { overflow: hidden }` rule (assets/base.css)
  // instead, toggled via the `scroll-lock` attribute handling at the bottom of this file.
  // #previousScrollY = 0;

  /**
   * Shows the dialog.
   */
  showDialog() {
    const { dialog } = this.refs;

    if (dialog.open) return;

    // CH: native scroll-position save + position:fixed body lock removed here — see note above.
    // const scrollY = window.scrollY;
    // this.#previousScrollY = scrollY;
    //
    // // Prevent layout thrashing by separating DOM reads from DOM writes
    // requestAnimationFrame(() => {
    //   document.body.style.width = '100%';
    //   document.body.style.position = 'fixed';
    //   document.body.style.top = `-${scrollY}px`;
    //
    //   dialog.showModal();
    //   this.dispatchEvent(new DialogOpenEvent());
    //
    //   this.addEventListener('click', this.#handleClick);
    //   this.addEventListener('keydown', this.#handleKeyDown);
    // });

    dialog.showModal();

    // CH: lock page scroll directly for dialogs carrying `scroll-lock` (e.g. ch-cart-drawer.liquid)
    // rather than relying only on the `toggle` listener at the bottom of this file — Safari < 26
    // doesn't fire `toggle` on <dialog>, so the page behind the cart drawer stayed scrollable.
    // Unlocks on the dialog's `close` event so every close path is covered (closeDialog(),
    // a direct dialog.close(), etc.), mirroring frontend/js/mobile-menu.js.
    if (dialog.hasAttribute('scroll-lock')) {
      document.documentElement.setAttribute('scroll-lock', '');
      dialog.addEventListener('close', () => document.documentElement.removeAttribute('scroll-lock'), {
        once: true,
      });
    }

    this.dispatchEvent(new DialogOpenEvent());

    this.addEventListener('click', this.#handleClick);
    this.addEventListener('keydown', this.#handleKeyDown);
  }

  /**
   * Closes the dialog.
   */
  closeDialog = async () => {
    const { dialog } = this.refs;

    if (!dialog.open) return;

    this.removeEventListener('click', this.#handleClick);
    this.removeEventListener('keydown', this.#handleKeyDown);

    // Force browser to restart animation by resetting it
    // Temporarily remove any existing animation state
    dialog.style.animation = 'none';

    // Force a reflow
    void dialog.offsetWidth;

    // Now add the closing class and restore animation
    dialog.classList.add('dialog-closing');
    dialog.style.animation = '';

    await onAnimationEnd(dialog, undefined, {
      subtree: false,
    });

    // CH: native scroll-position restore removed to match the showDialog() change above —
    // scroll is never artificially locked/offset, so there's nothing to restore.
    // document.body.style.width = '';
    // document.body.style.position = '';
    // document.body.style.top = '';
    // window.scrollTo({ top: this.#previousScrollY, behavior: 'instant' });

    dialog.close();
    dialog.classList.remove('dialog-closing');

    this.dispatchEvent(new DialogCloseEvent());
  };

  /**
   * Toggles the dialog.
   */
  toggleDialog = () => {
    if (this.refs.dialog.open) {
      this.closeDialog();
    } else {
      this.showDialog();
    }
  };

  /**
   * Closes the dialog when the user clicks outside of it.
   *
   * @param {MouseEvent} event - The mouse event.
   */
  #handleClick(event) {
    const { dialog } = this.refs;

    // CH: a click whose target was removed from the page while the click was
    // being handled isn't a click outside. e.g. with reduced motion on,
    // removing a cart row deletes it (and its Remove button) immediately, so
    // `dialog.contains(target)` fails and the cart drawer used to close.
    // composedPath() still records where the click actually happened.
    if (event.target instanceof Element && !event.target.isConnected && event.composedPath().includes(dialog)) return;

    if (isClickedOutside(event, dialog)) {
      this.closeDialog();
    }
  }

  /**
   * Closes the dialog when the user presses the escape key.
   *
   * @param {KeyboardEvent} event - The keyboard event.
   */
  #handleKeyDown(event) {
    if (event.key !== 'Escape') return;

    event.preventDefault();
    this.closeDialog();
  }

  /**
   * Gets the minimum width of the dialog.
   *
   * @returns {number} The minimum width of the dialog.
   */
  get minWidth() {
    return Number(this.getAttribute('dialog-active-min-width'));
  }

  /**
   * Gets the maximum width of the dialog.
   *
   * @returns {number} The maximum width of the dialog.
   */
  get maxWidth() {
    return Number(this.getAttribute('dialog-active-max-width'));
  }
}

if (!customElements.get('dialog-component')) customElements.define('dialog-component', DialogComponent);

export class DialogOpenEvent extends CustomEvent {
  constructor() {
    super(DialogOpenEvent.eventName);
  }

  static eventName = 'dialog:open';
}

export class DialogCloseEvent extends CustomEvent {
  constructor() {
    super(DialogCloseEvent.eventName);
  }

  static eventName = 'dialog:close';
}

document.addEventListener(
  'toggle',
  (event) => {
    // CH: added `HTMLDialogElement` — native only handled `<details>`, but our
    // ch-cart-drawer.liquid dialog carries `scroll-lock` too and needs the same handling.
    if (event.target instanceof HTMLDetailsElement || event.target instanceof HTMLDialogElement) {
      if (event.target.hasAttribute('scroll-lock')) {
        const { open } = event.target;
        if (open) {
          document.documentElement.setAttribute('scroll-lock', '');
        } else {
          document.documentElement.removeAttribute('scroll-lock');
        }
      }
    }
  },
  { capture: true }
);
